import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase/client';
import { Loader2, CheckCircle2, XCircle, Clock, Hotel } from 'lucide-react';
import { cn } from '@/lib/utils';
import type {
  SearchStatus,
  SearchProgressLogEntry,
  HotelSearchProgress,
} from '@/types/hotel-search';

interface Props {
  searchId: string;
  onCompleted: () => void;
}

interface DestinationProgress {
  municipality: string;
  total: number;
  done: number;
  hotels: SearchProgressLogEntry[];
}

export function HotelSearchProgress({ searchId, onCompleted }: Props) {
  const [progress, setProgress] = useState<HotelSearchProgress>({
    status: 'processing',
    progress_total: 0,
    progress_done: 0,
    progress_log: [],
  });

  // Load initial state + subscribe to realtime changes
  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;

    async function loadInitial() {
      const { data } = await supabase
        .from('hotel_searches')
        .select('status, progress_total, progress_done, progress_log')
        .eq('search_id', searchId)
        .maybeSingle();

      if (data) {
        const p: HotelSearchProgress = {
          status: data.status as SearchStatus,
          progress_total: data.progress_total ?? 0,
          progress_done: data.progress_done ?? 0,
          progress_log: (data.progress_log ?? []) as SearchProgressLogEntry[],
        };
        setProgress(p);
        if (p.status === 'completed' || p.status === 'failed') {
          onCompleted();
          return;
        }
      }

      // Subscribe to postgres changes on this row
      channel = supabase
        .channel(`hotel-search-progress-${searchId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'hotel_searches',
            filter: `search_id=eq.${searchId}`,
          },
          payload => {
            const row = payload.new as any;
            const updated: HotelSearchProgress = {
              status: row.status as SearchStatus,
              progress_total: row.progress_total ?? 0,
              progress_done: row.progress_done ?? 0,
              progress_log: (row.progress_log ?? []) as SearchProgressLogEntry[],
            };
            setProgress(updated);
            if (updated.status === 'completed' || updated.status === 'failed') {
              channel?.unsubscribe();
              setTimeout(onCompleted, 800); // small delay for the final animation
            }
          },
        )
        .subscribe();
    }

    loadInitial();
    return () => { channel?.unsubscribe(); };
  }, [searchId, onCompleted]);

  // Group log by municipality
  const byMunicipality = progress.progress_log.reduce<Record<string, DestinationProgress>>(
    (acc, entry) => {
      if (!acc[entry.municipality]) {
        acc[entry.municipality] = { municipality: entry.municipality, total: 0, done: 0, hotels: [] };
      }
      acc[entry.municipality].hotels.push(entry);
      acc[entry.municipality].done++;
      return acc;
    },
    {},
  );

  // Estimate totals per municipality from total/destinations count
  const municipalities = Object.values(byMunicipality);
  const globalPct = progress.progress_total > 0
    ? Math.round((progress.progress_done / progress.progress_total) * 100)
    : 0;

  const isFailed = progress.status === 'failed';

  return (
    <div className="flex flex-col items-center justify-center h-full px-10 py-8">
      <div className="w-full max-w-xl">

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          {isFailed ? (
            <XCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
          ) : (
            <Loader2 className="w-6 h-6 text-voah-teal animate-spin flex-shrink-0" />
          )}
          <div>
            <h2 className="text-base font-semibold text-gray-800">
              {isFailed ? 'Falha no enriquecimento' : 'Preparando seleção de hotéis...'}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {progress.progress_done} de {progress.progress_total} hotel{progress.progress_total !== 1 ? 'is' : ''} processado{progress.progress_done !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {/* Global progress bar */}
        <div className="w-full bg-gray-100 rounded-full h-1.5 mb-6 overflow-hidden">
          <div
            className={cn(
              'h-full rounded-full transition-all duration-500',
              isFailed ? 'bg-red-400' : 'bg-voah-teal',
            )}
            style={{ width: `${globalPct}%` }}
          />
        </div>

        {/* Per-municipality breakdown */}
        {municipalities.length > 0 && (
          <div className="space-y-4 mb-6">
            {municipalities.map(dest => (
              <div key={dest.municipality}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-semibold text-gray-600">{dest.municipality}</span>
                  <span className="text-xs text-gray-400">{dest.done} hotel{dest.done !== 1 ? 'is' : ''}</span>
                </div>
                <div className="space-y-1">
                  {dest.hotels.map((h, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-gray-500">
                      <StatusIcon status={h.status} />
                      <span className={cn(h.status === 'unavailable' && 'line-through text-gray-300')}>
                        {h.name || h.hotel_id}
                      </span>
                      {h.status === 'success' && h.images != null && (
                        <span className="text-gray-300 text-[10px]">· {h.images} foto{h.images !== 1 ? 's' : ''}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Current activity */}
        {progress.status === 'processing' && progress.progress_done < progress.progress_total && (
          <div className="flex items-center gap-2 text-xs text-gray-400 border border-dashed border-gray-200 rounded-lg px-3 py-2">
            <Hotel className="w-3.5 h-3.5 flex-shrink-0 text-voah-teal animate-pulse" />
            <span>Enriquecendo próximo hotel via Booking.com...</span>
          </div>
        )}

        {progress.status === 'completed' && (
          <div className="flex items-center gap-2 text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
            <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
            <span>Concluído! Carregando seleção...</span>
          </div>
        )}

      </div>
    </div>
  );
}

function StatusIcon({ status }: { status: SearchProgressLogEntry['status'] }) {
  if (status === 'success') return <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" />;
  if (status === 'unavailable') return <XCircle className="w-3 h-3 text-red-400 flex-shrink-0" />;
  return <Clock className="w-3 h-3 text-gray-300 flex-shrink-0" />;
}
